import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, Polygon, DrawingManager, useJsApiLoader } from '@react-google-maps/api';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import messages from '../../../locale/messages';

// Default Map Options
const containerStyle = {
  width: '100%',
  height: '400px'
};

const defaultCenter = {
  lat: 37.0902,
  lng: -95.7129
};

const libraries = ['drawing', 'places'];

function DrawPolygonMap(props) {
  const { zoom = 4, height = '400px', width = '100%', formName, fieldName, paths = [], lat, lng, change } = props;

  const [polygonPaths, setPolygonPaths] = useState(paths || []);
  const [drawingMode, setDrawingMode] = useState(true);
  const [map, setMap] = useState(null);

  const polygonRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, // or pass your API key
    libraries,
  });

  const center = lat && lng ? { lat, lng } : defaultCenter;

  const onLoadMap = (mapInstance) => {
    setMap(mapInstance);
    if (polygonPaths.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      polygonPaths.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
      mapInstance.fitBounds(bounds);
    }
  };

  const formatPolygonPaths = (path) => {
    const points = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      points.push({ lat: point.lat(), lng: point.lng() });
    }
    return points;
  };

  const onPolygonComplete = (polygon) => {
    const newPaths = formatPolygonPaths(polygon.getPath());
    setPolygonPaths(newPaths);
    setDrawingMode(false);

    if (formName && fieldName) {
      change(formName, fieldName, newPaths);
    }

    polygonRef.current = polygon;

    polygon.getPath().addListener('set_at', () => {
      const updatedPaths = formatPolygonPaths(polygon.getPath());
      setPolygonPaths(updatedPaths);
      if (formName && fieldName) {
        change(formName, fieldName, updatedPaths);
      }
    });

    polygon.getPath().addListener('insert_at', () => {
      const updatedPaths = formatPolygonPaths(polygon.getPath());
      setPolygonPaths(updatedPaths);
      if (formName && fieldName) {
        change(formName, fieldName, updatedPaths);
      }
    });

    polygon.getPath().addListener('remove_at', () => {
      const updatedPaths = formatPolygonPaths(polygon.getPath());
      setPolygonPaths(updatedPaths);
      if (formName && fieldName) {
        change(formName, fieldName, updatedPaths);
      }
    });
  };

  const onOverlayComplete = (e) => {
    if (e.type === 'polygon') {
      e.overlay.setEditable(true);
      onPolygonComplete(e.overlay);
    }
  };

  const removePolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }
    setPolygonPaths([]);
    setDrawingMode(true);
    if (formName && fieldName) {
      change(formName, fieldName, null);
    }
  };

  const onEditPolygon = () => {
    if (polygonRef.current) {
      const updatedPaths = formatPolygonPaths(polygonRef.current.getPath());
      setPolygonPaths(updatedPaths);
      if (formName && fieldName) {
        change(formName, fieldName, updatedPaths);
      }
    }
  };

  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <div style={{ height, width }}>
      <GoogleMap
        mapContainerStyle={{ height, width }}
        center={center}
        zoom={zoom}
        onLoad={onLoadMap}
      >
        {polygonPaths.length > 0 && (
          <>
            <button
              type="button"
              onClick={removePolygon}
              className='removeLocationBtn'
            >
              <FormattedMessage {...messages.removeLocation} />
            </button>

            <Polygon
              paths={polygonPaths}
              options={{
                fillColor: "blue",
                strokeColor: "#FF0000",
                fillOpacity: 0.35,
                strokeWeight: 1,
                editable: true,
                draggable: false,
              }}
              onLoad={(polygon) => (polygonRef.current = polygon)}
              onMouseUp={onEditPolygon}
              onDragEnd={onEditPolygon}
            />
          </>
        )}
        {drawingMode && (
          <DrawingManager
            drawingMode="polygon"
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: ['polygon'],
              },
              polygonOptions: {
                fillColor: "#FF0000",
                strokeColor: "#FF0000",
                fillOpacity: 0.35,
                strokeWeight: 1,
                clickable: true,
                editable: true,
                draggable: true,
                zIndex: 1,
              },
            }}
            onOverlayComplete={onOverlayComplete}
          />
        )}
      </GoogleMap>
    </div>
  );
}

const mapState = (state) => ({});

const mapDispatch = {
  change,
};

export default connect(mapState, mapDispatch)(DrawPolygonMap);
